import { useEffect, useRef, useState } from "react";
import { db } from "./firebase";
import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs,
  orderBy, query, serverTimestamp, setDoc, where,
} from "firebase/firestore";

const DEFAULT_TYPES = ["Running", "Walking", "Rowing", "Biking", "Swimming", "Elliptical", "Stair Climber"];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function formatDuration(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDurationShort(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

const cardStyle = {
  background: "#f8fbff",
  borderRadius: "18px",
  padding: "18px",
  border: "2px solid #bfdbfe",
  marginBottom: "14px",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f5faff",
  color: "#1e3a5f",
  fontSize: "15px",
  boxSizing: "border-box",
  outline: "none",
};

export default function CardioLog({ uid, show, onClose }) {
  const [entries, setEntries] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [defaultType, setDefaultType] = useState("Running");
  const [selectedType, setSelectedType] = useState(null);
  const [customTypeInput, setCustomTypeInput] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [manualMinutes, setManualMinutes] = useState("");
  const [useTimer, setUseTimer] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!uid || !show) return;
    loadData();
  }, [uid, show]);

  async function loadData() {
    setLoading(true);
    try {
      const today = todayStr();
      const q = query(
        collection(db, "cardioLogs"),
        where("uid", "==", uid),
        where("date", "==", today),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const settingsSnap = await getDoc(doc(db, "cardioSettings", uid));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        const saved = data.customTypes || [];
        const def = data.defaultType || "Running";
        setCustomTypes(saved);
        setDefaultType(def);
        setSelectedType((prev) => prev ?? def);
      } else {
        setSelectedType((prev) => prev ?? "Running");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => setTimerSeconds((s) => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning]);

  async function saveEntry() {
    const durationSeconds = useTimer
      ? timerSeconds
      : Math.round((parseFloat(manualMinutes) || 0) * 60);
    if (durationSeconds <= 0) return;
    setSaving(true);
    setSaveError("");
    try {
      const type = selectedType || defaultType;
      const docRef = await addDoc(collection(db, "cardioLogs"), {
        uid,
        date: todayStr(),
        type,
        durationSeconds,
        createdAt: serverTimestamp(),
      });
      setEntries((prev) => [{ id: docRef.id, type, durationSeconds, date: todayStr() }, ...prev]);
      setManualMinutes("");
      setTimerSeconds(0);
      setTimerRunning(false);
      setUseTimer(false);
    } catch {
      setSaveError("Couldn't save cardio entry. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteEntry(id) {
    await deleteDoc(doc(db, "cardioLogs", id));
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  async function addCustomType(typeName) {
    const trimmed = typeName.trim();
    if (!trimmed || [...DEFAULT_TYPES, ...customTypes].includes(trimmed)) return;
    const newTypes = [...customTypes, trimmed];
    setCustomTypes(newTypes);
    setSelectedType(trimmed);
    setShowCustomInput(false);
    setCustomTypeInput("");
    await setDoc(doc(db, "cardioSettings", uid), { customTypes: newTypes }, { merge: true });
  }

  async function setAsDefault(typeName) {
    setDefaultType(typeName);
    await setDoc(doc(db, "cardioSettings", uid), { defaultType: typeName }, { merge: true });
  }

  if (!show) return null;

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ color: "#4b6b8a", fontSize: "14px", textAlign: "center", padding: "20px 0" }}>
          Loading cardio log...
        </div>
      </div>
    );
  }

  const allTypes = [...DEFAULT_TYPES, ...customTypes];
  const currentType = selectedType || defaultType;
  const durationReady = useTimer ? timerSeconds > 0 : Boolean(manualMinutes && parseFloat(manualMinutes) > 0);

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "17px" }}>🏃 Log Cardio</div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: "none", border: "none", color: "#6b8cb0", fontSize: "22px", cursor: "pointer", lineHeight: 1 }}
        >
          ✕
        </button>
      </div>

      {/* Activity type */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Activity
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {allTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              style={{
                display: "flex", alignItems: "center", gap: "4px",
                padding: "7px 11px", borderRadius: "10px",
                border: currentType === type ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: currentType === type ? "#e0f2fe" : "#f5faff",
                color: "#1e3a5f",
                fontWeight: currentType === type ? "bold" : "normal",
                fontSize: "13px", cursor: "pointer",
              }}
            >
              {type === defaultType && <span style={{ color: "#f59e0b", fontSize: "12px" }}>★</span>}
              {type}
              {type !== defaultType && (
                <span
                  onClick={(e) => { e.stopPropagation(); setAsDefault(type); }}
                  style={{ color: "#d1d5db", fontSize: "11px", cursor: "pointer", marginLeft: "2px" }}
                  title="Set as default"
                >
                  ☆
                </span>
              )}
            </button>
          ))}
          {!showCustomInput && (
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              style={{
                padding: "7px 11px", borderRadius: "10px",
                border: "2px dashed #bfdbfe", background: "transparent",
                color: "#6b8cb0", fontSize: "13px", cursor: "pointer",
              }}
            >
              + Custom
            </button>
          )}
        </div>
        {showCustomInput && (
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <input
              type="text"
              placeholder="e.g. Jump Rope"
              value={customTypeInput}
              onChange={(e) => setCustomTypeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustomType(customTypeInput)}
              autoFocus
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => addCustomType(customTypeInput)}
              style={{ padding: "11px 14px", borderRadius: "12px", border: "none", background: "#38bdf8", color: "#fff", fontWeight: "bold", cursor: "pointer" }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowCustomInput(false); setCustomTypeInput(""); }}
              style={{ padding: "11px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Duration */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Duration
        </div>
        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          <button
            type="button"
            onClick={() => { setUseTimer(false); setTimerRunning(false); }}
            style={{
              flex: 1, padding: "9px", borderRadius: "10px",
              border: !useTimer ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: !useTimer ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f", fontWeight: !useTimer ? "bold" : "normal",
              cursor: "pointer", fontSize: "13px",
            }}
          >
            Manual
          </button>
          <button
            type="button"
            onClick={() => setUseTimer(true)}
            style={{
              flex: 1, padding: "9px", borderRadius: "10px",
              border: useTimer ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: useTimer ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f", fontWeight: useTimer ? "bold" : "normal",
              cursor: "pointer", fontSize: "13px",
            }}
          >
            Timer
          </button>
        </div>

        {!useTimer ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="number"
              placeholder=""
              value={manualMinutes}
              onChange={(e) => setManualMinutes(e.target.value)}
              onFocus={(e) => e.target.select()}
              min="0"
              step="0.5"
              style={{ width: "90px", padding: "11px 12px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "20px", fontWeight: "bold", textAlign: "center" }}
            />
            <span style={{ color: "#4b6b8a", fontSize: "14px" }}>minutes</span>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ fontSize: "44px", fontWeight: "bold", color: "#1e3a5f", fontVariantNumeric: "tabular-nums", marginBottom: "12px", letterSpacing: "-1px" }}>
              {formatDuration(timerSeconds)}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => setTimerRunning((r) => !r)}
                style={{
                  padding: "11px 26px", borderRadius: "12px", border: "none",
                  background: timerRunning ? "#fbbf24" : "#34d399",
                  color: "#fff", fontWeight: "bold", fontSize: "15px", cursor: "pointer",
                }}
              >
                {timerRunning ? "Pause" : timerSeconds > 0 ? "Resume" : "Start"}
              </button>
              {timerSeconds > 0 && !timerRunning && (
                <button
                  type="button"
                  onClick={() => setTimerSeconds(0)}
                  style={{
                    padding: "11px 18px", borderRadius: "12px",
                    border: "2px solid #bfdbfe", background: "#f5faff",
                    color: "#4b6b8a", fontWeight: "bold", fontSize: "15px", cursor: "pointer",
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {saveError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", color: "#dc2626", fontSize: "13px", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {saveError}
          <button onClick={() => setSaveError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Log button */}
      <button
        type="button"
        onClick={saveEntry}
        disabled={saving || !durationReady}
        style={{
          width: "100%", padding: "13px", borderRadius: "14px", border: "none",
          background: saving || !durationReady ? "#cbd5e1" : "#2563eb",
          color: "#fff", fontWeight: "bold", fontSize: "15px", cursor: saving || !durationReady ? "not-allowed" : "pointer",
          marginBottom: "16px",
        }}
      >
        {saving ? "Saving..." : "Log Cardio"}
      </button>

      {/* Today's sessions */}
      {entries.length === 0 && (
        <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", padding: "8px 0" }}>
          No cardio logged yet today. Add your first session above.
        </div>
      )}
      {entries.length > 0 && (
        <div>
          <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Today's Sessions
          </div>
          <div style={{ display: "grid", gap: "8px" }}>
            {entries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", borderRadius: "12px",
                  background: "#f0f9ff", border: "1px solid #dbeafe",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{entry.type}</div>
                  <div style={{ color: "#4b6b8a", fontSize: "12px" }}>{formatDurationShort(entry.durationSeconds)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteEntry(entry.id)}
                  style={{
                    width: "28px", height: "28px", borderRadius: "8px", border: "none",
                    background: "#fecaca", color: "#dc2626", fontWeight: "bold",
                    fontSize: "14px", cursor: "pointer", flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
